/*
PURPOSE:
This plugin intercepts all $fetch calls in our application and automatically
adds the authentication token to API requests. It eliminates the need to
manually add auth headers in every component that makes API calls.

WHY IT'S NEEDED:
Without this plugin, we would need to manually add the Authorization header to
every API call throughout our application. This centralizes our auth token logic,
making our code cleaner and ensuring we never forget to include the token.

USAGE EXAMPLE:

  // This will automatically include the auth token
  const data = await $fetch('/api/hello-world')

  // Instead of having to write:
  const data = await fetch('/api/hello-world', {
    headers: { 'Authorization': `Bearer ${token}` }
  })

*/

export default defineNuxtPlugin(() => {
  const auth = useAuth()

  // Wrap the global ofetch instance with `.create` (NOT a plain function) so the
  // returned $fetch keeps ofetch's API — `.raw`, `.create`, etc. @sidebase/nuxt-auth
  // calls `$fetch.raw(...)` internally; replacing $fetch with a bare async function
  // strips those methods and breaks sign-in ("$fetch.raw is not a function").
  globalThis.$fetch = $fetch.create({
    onRequest({ request, options }) {
      const url = typeof request === 'string' ? request : (request?.url ?? '')

      // Only add the auth header for our own (non-auth) API routes
      if (
        url.startsWith('/api/') &&
        !url.startsWith('/api/auth/') &&
        auth.status.value === 'authenticated' &&
        auth.token.value
      ) {
        const headers = new Headers(options.headers)
        // sidebase's auth.token.value already includes the Bearer prefix
        headers.set('Authorization', auth.token.value)
        options.headers = headers
      }
    },
  })
})
